/* ═══════════════════════════════════════════════════════════
   js/components/booking-form.js — <booking-form> Web Component
   Форма бронирования с валидацией, CSRF, телефон-маской

   jk-redesign 6.7: кандидат на PHP-партиал components/lead-form.php — рендер
   в Shadow DOM из JS (вне PHP-шаблонов), стили инкапсулированы. FINDING 6.7:
   consent-ссылка здесь ведёт на /policy.php — рассинхрон с юр-паттерном ЭТАПА 4
   (все PHP-формы → /soglasie.php); исправление меняет рендер → отдельный блок.
   ═══════════════════════════════════════════════════════════ */
class BookingForm extends HTMLElement {
  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
    this._state = 'form'; // form | loading | success | error
  }

  static get observedAttributes() { return ['data-jk-id', 'data-jk-name', 'data-compact']; }

  connectedCallback() {
    this._render();
  }

  get jkId()   { return this.getAttribute('data-jk-id') || ''; }
  get jkName() { return this.getAttribute('data-jk-name') || ''; }
  get compact() { return this.hasAttribute('data-compact'); }

  _render() {
    const compact = this.compact;
    this.shadowRoot.innerHTML = `
      <style>
        :host { display: block; }
        .form { display: flex; flex-direction: column; gap: 12px; }
        .form.compact { flex-direction: row; flex-wrap: wrap; gap: 8px; }
        .form.compact input { flex: 1; min-width: 140px; }
        .form.compact button { flex: 0 0 auto; }
        .form.compact .consent { flex: 0 0 100%; }
        input {
          background: var(--dark3, #1a1a24); border: 1px solid var(--border, rgba(255,255,255,.08));
          color: var(--text, #e8e6e1); padding: 12px 16px; border-radius: 10px;
          font-size: 14px; outline: none; transition: border-color .2s;
          font-family: var(--font-body, 'Mulish', sans-serif);
        }
        input:focus { border-color: var(--gold, #c8a96e); }
        input.error { border-color: var(--error, #ef4444); }
        button {
          background: linear-gradient(135deg, var(--gold-dark, #b08d4e), var(--gold-light, #d4b978));
          color: var(--dark, #0a0a0f); border: none; padding: 14px 24px; border-radius: 10px;
          font-weight: 700; font-size: 14px; cursor: pointer; transition: transform .15s, box-shadow .15s;
          font-family: var(--font-body, 'Mulish', sans-serif);
        }
        button:hover { transform: translateY(-1px); box-shadow: 0 4px 20px rgba(200,169,110,.3); }
        button:disabled { opacity: .5; cursor: wait; transform: none; }
        .success { text-align: center; padding: 20px; color: var(--gold, #c8a96e); }
        .success-icon { font-size: 40px; margin-bottom: 8px; }
        .error-msg { color: var(--error, #ef4444); font-size: 12px; margin-top: -4px; }
        .policy { font-size: 11px; color: var(--text-muted, #8a8a8a); line-height: 1.4; }
        .policy a { color: var(--gold, #c8a96e); text-decoration: none; }
        .consent { display: flex; align-items: flex-start; gap: 8px; font-size: 11px; color: var(--text-muted, #8a8a8a); line-height: 1.4; cursor: pointer; }
        .consent input { width: 16px; height: 16px; flex: 0 0 auto; margin-top: 1px; accent-color: var(--gold, #c8a96e); cursor: pointer; }
        .consent.error { color: var(--error, #ef4444); }
        .consent a { color: var(--gold, #c8a96e); text-decoration: none; }
        .hp { position: absolute; left: -9999px; }
      </style>
      <div id="root"></div>
    `;
    this._renderState();
  }

  _renderState() {
    const root = this.shadowRoot.getElementById('root');
    if (this._state === 'success') {
      root.innerHTML = '<div class="success"><div class="success-icon">&#10003;</div><div>Заявка отправлена! Перезвоним в течение 15 минут.</div></div>';
      return;
    }
    if (this._state === 'error') {
      root.innerHTML = '<div class="success" style="color:var(--error)"><div class="success-icon">&#9888;</div><div>Ошибка отправки. Попробуйте позже.</div></div>';
      return;
    }

    const c = this.compact ? ' compact' : '';
    root.innerHTML = `
      <form class="form${c}" novalidate>
        <input type="text" class="hp" name="_hp" tabindex="-1" autocomplete="off">
        <input type="text" name="name" placeholder="Ваше имя" autocomplete="name">
        <input type="tel" name="phone" placeholder="+7 (___) ___-__-__" required autocomplete="tel">
        <label class="consent">
          <input type="checkbox" name="consent_main" required>
          <span>Согласен на <a href="/soglasie.php" target="_blank" rel="noopener">обработку персональных данных</a> (имя, телефон) для подбора недвижимости.</span>
        </label>
        <button type="submit">${this.compact ? 'Отправить' : 'Оставить заявку'}</button>
      </form>
    `;

    const form = root.querySelector('form');
    const phoneInput = root.querySelector('[name="phone"]');

    // Телефон-маска
    phoneInput.addEventListener('input', () => {
      let v = phoneInput.value.replace(/\D/g, '');
      if (v.startsWith('8')) v = '7' + v.slice(1);
      if (!v.startsWith('7')) v = '7' + v;
      let formatted = '+7';
      if (v.length > 1) formatted += ' (' + v.slice(1, 4);
      if (v.length > 4) formatted += ') ' + v.slice(4, 7);
      if (v.length > 7) formatted += '-' + v.slice(7, 9);
      if (v.length > 9) formatted += '-' + v.slice(9, 11);
      phoneInput.value = formatted;
    });

    form.addEventListener('submit', (e) => {
      e.preventDefault();
      this._submit(form);
    });
  }

  async _submit(form) {
    const name  = form.querySelector('[name="name"]').value.trim();
    const phone = form.querySelector('[name="phone"]').value.trim();
    const hp    = form.querySelector('[name="_hp"]').value;
    // Fix-раунд 4.1-4.2 (2026-07-02, пункт F): email убран с лид-формы (минимизация ПДн).
    const consentEl = form.querySelector('[name="consent_main"]');
    const consent   = !!(consentEl && consentEl.checked);

    // Валидация
    const phoneDigits = phone.replace(/\D/g, '');
    if (phoneDigits.length < 11) {
      form.querySelector('[name="phone"]').classList.add('error');
      return;
    }
    form.querySelector('[name="phone"]').classList.remove('error');

    // 152-ФЗ: согласие обязательно (send.php → 422 без consent_main).
    const consentLabel = form.querySelector('.consent');
    if (!consent) {
      if (consentLabel) consentLabel.classList.add('error');
      return;
    }
    if (consentLabel) consentLabel.classList.remove('error');

    // Loading
    const btn = form.querySelector('button');
    btn.disabled = true;
    btn.textContent = 'Отправка...';

    try {
      const body = {
        _hp: hp,
        name: name,
        phone: phone,
        consent_main: '1',
        _source: this.jkName ? 'Форма ЖК: ' + this.jkName : 'Форма бронирования'
      };
      if (this.jkId) body.jk_id = this.jkId;

      const res = await fetch('/send.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });

      if (res.ok) {
        this._state = 'success';
        if (typeof ymGoal === 'function') ymGoal('booking_form_submit');
        this.dispatchEvent(new CustomEvent('booking-success', { bubbles: true, detail: { jkId: this.jkId } }));
      } else {
        this._state = 'error';
      }
    } catch (e) {
      this._state = 'error';
    }

    this._renderState();
  }
}

if (!customElements.get('booking-form')) {
  customElements.define('booking-form', BookingForm);
}
